import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["drawer", "backdrop", "trigger"]

  connect() {
    this.isOpen = false
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleFocusTrap = this.handleFocusTrap.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    this.drawerTarget.removeEventListener("keydown", this.handleFocusTrap)
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.isOpen) return

    this.isOpen = true

    // Show backdrop and drawer
    this.backdropTarget.classList.remove("d-none")
    this.drawerTarget.classList.remove("d-none")

    // Trigger slide-in on next frame (allows CSS transition)
    requestAnimationFrame(() => {
      this.drawerTarget.style.transform = "translateX(0)"
    })

    // Update aria
    this.drawerTarget.setAttribute("aria-hidden", "false")
    this.backdropTarget.setAttribute("aria-hidden", "false")

    // Enable focus trap
    this.drawerTarget.addEventListener("keydown", this.handleFocusTrap)

    // Focus the close button
    const closeBtn = this.drawerTarget.querySelector("[aria-label='Close drawer']")
    if (closeBtn) closeBtn.focus()
  }

  close() {
    if (!this.isOpen) return

    this.isOpen = false

    // Disable focus trap
    this.drawerTarget.removeEventListener("keydown", this.handleFocusTrap)

    // Slide out
    this.drawerTarget.style.transform = "translateX(100%)"

    // Hide after transition
    setTimeout(() => {
      this.drawerTarget.classList.add("d-none")
      this.backdropTarget.classList.add("d-none")
    }, 300)

    // Update aria
    this.drawerTarget.setAttribute("aria-hidden", "true")
    this.backdropTarget.setAttribute("aria-hidden", "true")

    // Return focus to trigger
    if (this.hasTriggerTarget) {
      this.triggerTarget.focus()
    }
  }

  handleKeydown(event) {
    // Don't trigger when typing in inputs
    const tag = event.target.tagName.toLowerCase()
    if (tag === "input" || tag === "textarea" || tag === "select" || event.target.isContentEditable) {
      return
    }

    if (event.key.toLowerCase() === "d") {
      event.preventDefault()
      this.toggle()
      return
    }

    // Esc closes drawer if open (let it propagate if closed)
    if (event.key === "Escape" && this.isOpen) {
      event.preventDefault()
      event.stopPropagation()
      this.close()
    }
  }

  // Focus trap: cycle Tab/Shift+Tab through focusable elements in the drawer
  handleFocusTrap(event) {
    if (event.key !== "Tab") return

    const focusable = this.drawerTarget.querySelectorAll(
      'a[href], button:not([disabled]), iframe, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }
}
